/**
 * Time Range Field Component (Single Range)
 * A generic, reusable single time range selector with:
 * - Type Autocomplete
 * - Start DateTimePicker
 * - End DateTimePicker
 * - Preset ranges
 * - Validation display
 *
 * This is a pure presentation component for use in FormBuilder.
 * Dynamic add/remove logic should be handled by parent business components.
 */
import React from 'react'
import {
  Grid2 as Grid,
  FormHelperText,
  Stack,
  Chip,
  Autocomplete,
  TextField
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import dayjs, { Dayjs } from 'dayjs'

export interface TimeRangeOption {
  value: string
  label: string
  startField?: string
  endField?: string
}

export interface PresetRange {
  label: string
  days: number
}

export interface TimeRangeFieldProps {
  type: string
  startDate: string | null
  endDate: string | null
  options: TimeRangeOption[]
  disabledTypes?: Set<string>
  onTypeChange: (type: string) => void
  onStartDateChange: (date: Dayjs | null) => void
  onEndDateChange: (date: Dayjs | null) => void
  onPresetSelect?: (days: number) => void
  onBlur?: () => void
  error?: string
  presetRanges?: PresetRange[]
  // Slot for custom action buttons (e.g., delete, add in business layer)
  actionSlot?: React.ReactNode
}

const TimeRangeField: React.FC<TimeRangeFieldProps> = ({
  type,
  startDate,
  endDate,
  options,
  disabledTypes = new Set(),
  onTypeChange,
  onStartDateChange,
  onEndDateChange,
  onPresetSelect,
  onBlur,
  error,
  presetRanges = [],
  actionSlot
}) => {
  const hasError = Boolean(error)

  return (
    <Grid container spacing={1} sx={{ alignItems: 'flex-start' }}>
      {/* Type Autocomplete */}
      <Grid size={{ xs: 12, sm: 3 }}>
        <Autocomplete
          value={options.find(opt => opt.value === type) || null}
          onChange={(_event, newValue) => {
            onTypeChange(newValue?.value || '')
          }}
          options={options}
          getOptionLabel={option => option.label}
          getOptionDisabled={option =>
            disabledTypes.has(option.value) && option.value !== type
          }
          isOptionEqualToValue={(option, value) => option.value === value.value}
          disableClearable={false}
          freeSolo={false}
          size='small'
          renderInput={params => (
            <TextField {...params} label='Time Type' size='small' />
          )}
        />
      </Grid>

      {/* Start DateTimePicker */}
      <Grid size={{ xs: 12, sm: actionSlot ? 4 : 4.5 }}>
        <DateTimePicker
          label='Start Date & Time'
          value={startDate ? dayjs(startDate) : null}
          onChange={onStartDateChange}
          onClose={onBlur}
          format='YYYY-MM-DD HH:mm:ss'
          views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
          ampm={false}
          timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              error: hasError
            },
            field: { clearable: true }
          }}
        />
      </Grid>

      {/* End DateTimePicker */}
      <Grid size={{ xs: 12, sm: actionSlot ? 4 : 4.5 }}>
        <DateTimePicker
          label='End Date & Time'
          value={endDate ? dayjs(endDate) : null}
          onChange={onEndDateChange}
          onClose={onBlur}
          format='YYYY-MM-DD HH:mm:ss'
          views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
          ampm={false}
          timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              error: hasError
            },
            field: { clearable: true }
          }}
        />
      </Grid>

      {/* Custom Action Slot (provided by business layer) */}
      {actionSlot && (
        <Grid size={{ xs: 12, sm: 1 }} sx={{ m: 'auto' }}>
          {actionSlot}
        </Grid>
      )}

      {/* Preset Range Chips */}
      {presetRanges.length > 0 && onPresetSelect && (
        <Grid size={{ xs: 12 }}>
          <Stack direction='row' spacing={1} sx={{ ml: 1 }}>
            {presetRanges.map(preset => (
              <Chip
                key={preset.days}
                label={preset.label}
                size='small'
                onClick={() => onPresetSelect(preset.days)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText'
                  }
                }}
              />
            ))}
          </Stack>
        </Grid>
      )}

      {/* Validation Error */}
      {error && (
        <Grid size={{ xs: 12 }}>
          <FormHelperText error sx={{ ml: 1 }}>
            {error}
          </FormHelperText>
        </Grid>
      )}
    </Grid>
  )
}

export default TimeRangeField
